import React, { useCallback, useEffect, useState } from 'react';

import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Label } from '@/design-system/label';
import { RadioField, RadioGroup } from '@/design-system/radio';
import { Textarea } from '@/design-system/textarea';
import {
  CancelStopResult,
  Shipment,
  ShipmentApiError,
  ShipmentReason,
  ShipmentRoute,
  ShipmentRouteStop,
  shipmentsAPI,
} from '@/services/api/shipments';

import {
  CANCEL_STOP_COPY,
  cancelStopConflictMessage,
  isNotBilled,
  offersCancelShipment,
} from './stopCancel';

/** Server cap on the note (CONTRACT-4W-NEGATIVE-SCENARIO-P0 §4). */
const NOTE_MAX = 500;

const STOP_TYPE_LABEL: Record<string, string> = {
  PICKUP: 'Pickup',
  DROP_OFF: 'Drop-off',
};

interface CancelStopModalProps {
  /** The shipment the route serves: waybill for the header, status for E27. */
  shipment: Pick<Shipment, 'waybill' | 'status'>;
  route: Pick<ShipmentRoute, 'routeId' | 'startedAt' | 'stops'>;
  /** The stop to cancel; when null the modal is closed. */
  stop: ShipmentRouteStop | null;
  onClose: () => void;
  /** The server cancelled the stop (200); the detail should re-load. */
  onCancelled: (result: CancelStopResult) => void;
  /** A 409 says the stop moved on since the detail loaded; re-load it. */
  onRefresh: () => void;
  /** E27: hand over to the existing Cancel shipment flow. */
  onCancelShipment: () => void;
}

/**
 * Ops "Cancel stop" (PRD TN2.11): pick a STOP_CANCELLATION reason (labels stay
 * Indonesian, "Not billed" on an internal input error), add a note for the
 * driver and client — required when the reason says so — and
 * `POST /v1/routes/:routeID/stops/:stopID/cancel`.
 *
 *   200                              → onCancelled (the panel re-loads)
 *   409 STOP_ALREADY_OPENED /
 *       STOP_RESOLVED /
 *       ROUTE_ALREADY_COMPLETED      → onRefresh + the matching message; the
 *                                      form closes down to "Close"
 *   422 / 400 / anything else        → the server's text, form stays editable
 *
 * E27 (eng review D3): for the last PENDING stop of a route that has not
 * started, the modal offers the existing Cancel shipment instead.
 */
const CancelStopModal: React.FC<CancelStopModalProps> = ({
  shipment,
  route,
  stop,
  onClose,
  onCancelled,
  onRefresh,
  onCancelShipment,
}) => {
  const open = stop !== null;
  const cancelShipmentInstead =
    !!stop && offersCancelShipment(shipment.status, route, stop.id);

  const [reasons, setReasons] = useState<ShipmentReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [reasonsError, setReasonsError] = useState(false);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set after a 409: the stop moved on, only "Close" is left. */
  const [conflict, setConflict] = useState<string | null>(null);

  const loadReasons = useCallback(async () => {
    setLoadingReasons(true);
    setReasonsError(false);
    try {
      setReasons(await shipmentsAPI.listReasons('STOP_CANCELLATION'));
    } catch {
      setReasons([]);
      setReasonsError(true);
    } finally {
      setLoadingReasons(false);
    }
  }, []);

  // A fresh form per stop. Keyed on the id only: the panel re-loads the
  // route after a 409, and that must not wipe the message it answers.
  const stopId = stop?.id ?? null;
  useEffect(() => {
    setReasonCode('');
    setNote('');
    setError(null);
    setConflict(null);
    setSubmitting(false);
  }, [stopId]);

  // Reasons are only needed for the per-stop cancel, not the E27 offer.
  useEffect(() => {
    if (stopId && !cancelShipmentInstead) void loadReasons();
  }, [stopId, cancelShipmentInstead, loadReasons]);

  const reason = reasons.find((r) => r.code === reasonCode);
  const noteRequired = !!reason?.requiresNote;
  const trimmedNote = note.trim();
  const canSubmit =
    !!reason &&
    (!noteRequired || trimmedNote.length > 0) &&
    !submitting &&
    !conflict;

  const close = () => {
    if (submitting) return;
    onClose();
  };

  const submit = async () => {
    if (!stop || !reason || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await shipmentsAPI.cancelStop(route.routeId, stop.id, {
        reasonCode: reason.code,
        note: trimmedNote || undefined,
      });
      onCancelled(result);
    } catch (err) {
      const apiError = err instanceof ShipmentApiError ? err : null;
      const message =
        apiError?.status === 409
          ? cancelStopConflictMessage(apiError.code)
          : null;
      if (message) {
        setConflict(message);
        onRefresh();
      } else {
        setError(
          err instanceof Error && err.message
            ? err.message
            : 'Could not cancel the stop.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabel = stop ? (STOP_TYPE_LABEL[stop.type] ?? stop.type) : '';

  const footer = conflict ? (
    <div className="flex items-center justify-end gap-2">
      <Button tone="neutral" style="stroke" onClick={close}>
        Close
      </Button>
    </div>
  ) : (
    <div className="flex items-center justify-end gap-2">
      <Button
        tone="neutral"
        style="stroke"
        onClick={close}
        disabled={submitting}
      >
        Back
      </Button>
      {cancelShipmentInstead ? (
        <Button tone="destructive" onClick={onCancelShipment}>
          Cancel shipment
        </Button>
      ) : (
        <Button
          tone="destructive"
          onClick={submit}
          disabled={!canSubmit}
          loading={submitting}
        >
          {CANCEL_STOP_COPY.action}
        </Button>
      )}
    </div>
  );

  return (
    <FormModal
      open={open}
      onClose={close}
      icon={<i className="ri-forbid-2-line" />}
      iconTone="danger"
      title={CANCEL_STOP_COPY.title}
      subtitle={
        stop
          ? `${shipment.waybill} · Stop ${stop.sequence} · ${typeLabel}`
          : undefined
      }
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={footer}
    >
      {stop && (
        <div className="space-y-4" data-testid="cancel-stop-modal">
          <p className="text-[12px] leading-snug text-text-sub-600">
            {stop.address}
          </p>

          {conflict ? (
            <Alert
              status="warning"
              appearance="lighter"
              size="lg"
              data-testid="cancel-stop-conflict"
              title={conflict}
            />
          ) : cancelShipmentInstead ? (
            <Alert
              status="information"
              appearance="lighter"
              size="lg"
              data-testid="cancel-stop-offer-shipment"
              title="This is the last open stop and the route hasn't started."
            >
              Cancel the whole shipment instead; its route is cancelled with
              it.
            </Alert>
          ) : (
            <>
              <p className="text-sm text-text-strong-950">
                {CANCEL_STOP_COPY.explanation}
              </p>

              <fieldset className="space-y-2">
                <legend className="mb-2 text-sm font-medium text-text-strong-950">
                  Cancellation reason{' '}
                  <span aria-hidden className="text-error-base">
                    *
                  </span>
                </legend>
                {loadingReasons ? (
                  <p className="text-[12px] text-text-sub-600">
                    Loading reasons…
                  </p>
                ) : reasonsError ? (
                  <Alert
                    status="error"
                    appearance="lighter"
                    size="lg"
                    title="Couldn't load the cancellation reasons."
                    action={
                      <Button
                        tone="neutral"
                        style="stroke"
                        size="xs"
                        onClick={() => void loadReasons()}
                      >
                        Retry
                      </Button>
                    }
                  />
                ) : reasons.length === 0 ? (
                  <p className="text-[12px] text-text-sub-600">
                    No cancellation reasons are set up.
                  </p>
                ) : (
                  <RadioGroup
                    value={reasonCode}
                    onValueChange={setReasonCode}
                    aria-label="Cancellation reason"
                    disabled={submitting}
                  >
                    {reasons.map((r) => (
                      <RadioField
                        key={r.code}
                        value={r.code}
                        label={
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <span>{r.label}</span>
                            {isNotBilled(r.category) && (
                              <Badge status="information" size="sm">
                                {CANCEL_STOP_COPY.notBilled}
                              </Badge>
                            )}
                          </span>
                        }
                        description={r.requiresNote ? 'Note required' : undefined}
                      />
                    ))}
                  </RadioGroup>
                )}
              </fieldset>

              <Field>
                <Label htmlFor="cancel-stop-note" required={noteRequired}>
                  Note for driver and client
                </Label>
                <Textarea
                  id="cancel-stop-note"
                  rows={3}
                  value={note}
                  maxLength={NOTE_MAX}
                  onChange={(e) => setNote(e.target.value)}
                  aria-required={noteRequired}
                  placeholder={
                    noteRequired
                      ? 'Required for this reason'
                      : 'Optional, e.g. client asks to deliver tomorrow'
                  }
                  disabled={submitting}
                />
              </Field>

              {error && (
                <Alert
                  status="error"
                  appearance="lighter"
                  size="lg"
                  data-testid="cancel-stop-error"
                  title={error}
                />
              )}
            </>
          )}
        </div>
      )}
    </FormModal>
  );
};

export default CancelStopModal;
